<template>
  <div class="flex flex-col h-full">
    <!-- Header with refresh date -->
    <div class="flex items-center justify-between px-4 pt-4 pb-2">
      <div class="flex items-center gap-3">
        <h2 class="text-lg font-semibold text-gray-800">Quality Operations</h2>
        <span v-if="lastRefreshDate" class="text-xs text-gray-400">
          Data as of {{ formatRefreshDate(lastRefreshDate) }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <!-- View Selector -->
        <div class="relative">
          <div
            v-if="showViewMenu"
            class="fixed inset-0 z-10"
            @click="closeViewMenu"
          ></div>
          <button
            @click="showViewMenu = !showViewMenu"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white hover:bg-gray-50 transition-colors cursor-pointer"
            style="border: 1px solid #E8E9E9; color: #353537;"
          >
            <span style="color: #A5A5A6;">View:</span>
            <span>{{ currentViewLabel }}</span>
            <span
              v-if="viewDirty"
              class="text-xs px-1 rounded"
              style="background-color: #FEF3F0; color: #F76734;"
            >modified</span>
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
          </button>
          <div
            v-if="showViewMenu"
            class="absolute top-full right-0 mt-1 bg-white rounded-md shadow-lg z-20"
            style="border: 1px solid #E8E9E9; min-width: 240px;"
          >
            <div class="px-3 py-1.5 text-xs uppercase tracking-wide" style="color: #A5A5A6;">Presets</div>
            <button
              v-for="v in builtinViews"
              :key="v.key"
              @click="selectView(v.key)"
              class="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between cursor-pointer"
              :style="currentViewKey === v.key ? { backgroundColor: '#FEF3F0', color: '#F76734', fontWeight: 600 } : { color: '#353537' }"
            >
              <span>{{ v.name }}</span>
              <span v-if="currentViewKey === v.key" class="text-xs">✓</span>
            </button>

            <div v-if="userViews.length" class="px-3 py-1.5 text-xs uppercase tracking-wide mt-1" style="color: #A5A5A6; border-top: 1px solid #F3F4F6;">My Views</div>
            <div
              v-for="v in userViews"
              :key="v.key"
              class="px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between cursor-pointer"
              :style="currentViewKey === v.key ? { backgroundColor: '#FEF3F0', color: '#F76734', fontWeight: 600 } : { color: '#353537' }"
              @click="selectView(v.key)"
            >
              <span>{{ v.name }}</span>
              <button
                @click.stop="deleteUserView(v.key)"
                class="text-xs px-1.5 hover:bg-gray-200 rounded cursor-pointer"
                style="color: #9CA3AF;"
                title="Delete view"
              >×</button>
            </div>

            <div class="px-2 py-2" style="border-top: 1px solid #F3F4F6;">
              <div v-if="!showSaveAsInput" class="flex flex-col gap-1">
                <button
                  @click="openSaveAs"
                  class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50 cursor-pointer"
                  style="color: #353537;"
                >+ Save current as…</button>
                <button
                  v-if="canSaveOverwrite && viewDirty"
                  @click="overwriteCurrentView"
                  class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50 cursor-pointer"
                  style="color: #353537;"
                >Save changes to "{{ currentView.name }}"</button>
              </div>
              <div v-else class="flex gap-1">
                <input
                  v-model="saveAsName"
                  @keyup.enter="confirmSaveAs"
                  @keyup.escape="cancelSaveAs"
                  ref="saveAsInput"
                  placeholder="View name"
                  class="flex-1 px-2 py-1 text-xs rounded"
                  style="border: 1px solid #E8E9E9;"
                />
                <button
                  @click="confirmSaveAs"
                  :disabled="!saveAsName.trim()"
                  class="px-2 py-1 text-xs rounded cursor-pointer"
                  style="background-color: #F76734; color: white;"
                >Save</button>
                <button
                  @click="cancelSaveAs"
                  class="px-2 py-1 text-xs rounded cursor-pointer"
                  style="color: #626264;"
                >×</button>
              </div>
            </div>
          </div>
        </div>

        <button
          @click="clearAllFilters"
          class="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-800 bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors cursor-pointer"
          title="Clear all filters"
        >
          Clear filters
        </button>

        <ExportMenu :disabled="!activeRows.length" :exports="[
          { type: 'csv', action: exportCsv },
          { type: 'csv', label: 'Patients', action: exportPatientsCsv, loading: patientsExporting },
          { type: 'pptx', action: exportPptx, loading: pptxLoading },
          { type: 'pdf', action: () => exportPptx(null, 'pdf'), loading: pptxLoading },
        ]" />
      </div>
    </div>

    <!-- Filter bar -->
    <div class="px-4 pb-2">
      <Worklist_FilterBar
        ref="filterBarRef"
        storage-key="quality_ops_entity_filter"
        :info-tag-types="infoTagTypes"
        :exclude-types="excludedEntityTypes"
        @filter-changed="onEntityFilterChanged"
      >
        <template #filters>
          <div class="flex items-center gap-1.5">
            <span class="text-xs text-gray-500 font-medium whitespace-nowrap">Year</span>
            <div class="w-28 [&_button]:py-1.5 [&_button]:text-xs">
              <SimpleMultiSelect
                v-model="performanceYears"
                :options="yearOptions"
                placeholder="Year"
                @change="onFilterChange"
              />
            </div>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="text-xs text-gray-500 font-medium whitespace-nowrap">Contract</span>
            <div class="w-52 [&_button]:py-1.5 [&_button]:text-xs">
              <SimpleMultiSelect
                v-model="selectedContractIds"
                :options="contractOptions"
                placeholder="All Contracts"
                :searchable="true"
                @change="onFilterChange"
              />
            </div>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="text-xs text-gray-500 font-medium whitespace-nowrap">Payer</span>
            <div class="w-40 [&_button]:py-1.5 [&_button]:text-xs">
              <SimpleMultiSelect
                v-model="selectedPayerNames"
                :options="payerOptions"
                placeholder="All Payers"
                :searchable="true"
                @change="onFilterChange"
              />
            </div>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="text-xs text-gray-500 font-medium whitespace-nowrap">Measure</span>
            <div class="w-52 [&_button]:py-1.5 [&_button]:text-xs">
              <SimpleMultiSelect
                v-model="selectedMeasureCodes"
                :options="measureOptions"
                placeholder="All Measures"
                :searchable="true"
                @change="onFilterChange"
              />
            </div>
          </div>
        </template>
      </Worklist_FilterBar>
    </div>

    <!-- Group chips -->
    <div class="flex items-center gap-2 px-4 pb-3 flex-wrap">
      <span class="text-xs text-gray-500 font-medium mr-1">Group by</span>
      <button
        v-for="opt in groupByOptions"
        :key="opt.value"
        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer"
        :class="chipClass(opt.value)"
        @click="toggleChip(opt.value)"
      >
        <span v-if="chipIndex(opt.value) >= 0"
          class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-500 text-white text-[10px] font-bold"
        >{{ chipIndex(opt.value) + 1 }}</span>
        {{ opt.label }}
      </button>
    </div>

    <!-- History chart -->
    <div v-if="historyData.values.length > 0" class="px-4 pb-3">
      <div class="border border-gray-200 rounded-lg bg-white">
        <button
          @click="chartOpen = !chartOpen"
          class="w-full flex items-center justify-between px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
        >
          <div class="flex items-center gap-2">
            <font-awesome-icon :icon="['fas', chartOpen ? 'chevron-down' : 'chevron-right']" class="text-[10px] text-gray-400" />
            <span>Performance History</span>
            <span class="text-gray-400">{{ historyData.values.length }} data points</span>
          </div>
        </button>
        <div v-if="chartOpen" class="border-t border-gray-100">
          <Chart_V5
            :chart_data="historyChartData"
            :chart_config="historyChartConfig"
            overrideHeight="h-[320px]"
          />
        </div>
      </div>
    </div>

    <!-- Main content area -->
    <div class="flex flex-1 min-h-0 px-4 pb-4">
      <!-- Left nav (when drilled in) -->
      <div v-if="selectedGroup" class="w-56 shrink-0 mr-3 flex flex-col">
        <button
          class="flex items-center gap-2 text-xs text-gray-500 hover:text-orange-600 mb-2 cursor-pointer"
          @click="navigateBack"
        >
          <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-[10px]" />
          {{ selectedDetailRow ? selectedGroupName : groupByLabel }}
        </button>
        <div class="rounded-lg border-l-4 border-orange-400 bg-orange-50 px-3 py-2">
          <div class="text-sm font-medium text-gray-800">
            {{ selectedGroupName }}
          </div>
          <div class="text-xs text-gray-500 mt-0.5">
            {{ selectedGroup.rate != null ? selectedGroup.rate + '%' : '—' }} rate
            · {{ selectedGroup.open_gaps || 0 }} open gaps
          </div>
          <!-- Second-level breadcrumb: the measure drilled into within the breakdown -->
          <div v-if="selectedDetailRow" class="text-xs text-orange-700 mt-1.5 pt-1.5 border-t border-orange-200">
            {{ selectedDetailRow.measure_code }} —
            {{ selectedDetailRow.measure_name || 'patients' }}
          </div>
        </div>
        <button
          v-if="isContractGrouping && !selectedDetailRow"
          @click="exportPptx(selectedGroupName)"
          :disabled="pptxLoading"
          class="mt-2 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2.5 py-1 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          title="Export just this contract to PowerPoint"
        >{{ pptxLoading ? 'Building…' : 'Export this contract (PPTX)' }}</button>
      </div>

      <!-- Content column: forecast panel + table -->
      <div class="flex-1 min-w-0 flex flex-col gap-3">

        <!-- Forecast panel (when drilled into CPY) -->
        <div v-if="isDrilledIntoCpy && !forecastLoading && !forecastData?.forecast" class="border border-gray-200 rounded-lg bg-white shrink-0 px-4 py-2.5 text-xs text-gray-400">
          No scoring configuration for this contract performance year.
        </div>
        <div v-if="isDrilledIntoCpy && forecastData?.forecast" class="border border-gray-200 rounded-lg bg-white shrink-0">
          <button
            @click="forecastOpen = !forecastOpen"
            class="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            <div class="flex items-center gap-3">
              <font-awesome-icon :icon="['fas', forecastOpen ? 'chevron-down' : 'chevron-right']" class="text-[10px] text-gray-400" />
              <span>Forecast</span>
              <span class="text-gray-400">
                Current {{ forecastData.current?.label || '—' }}
                → Projected {{ forecastData.forecast?.label || '—' }}
              </span>
              <span v-if="forecastData.forecast?.savings_eligible"
                class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700"
              >Savings Eligible</span>
            </div>
            <span class="text-gray-400 text-[10px]">{{ forecastMeasures.length }} measures</span>
          </button>

          <div v-if="forecastOpen" class="border-t border-gray-100 px-4 py-3">
            <table class="w-full text-xs">
              <thead>
                <tr class="text-gray-500">
                  <th class="text-left py-1.5 font-medium">Measure</th>
                  <th class="text-right py-1.5 font-medium">Current Rate</th>
                  <th class="text-right py-1.5 font-medium">Gaps to Next</th>
                  <th class="text-right py-1.5 font-medium">Closeable</th>
                  <th class="text-center py-1.5 font-medium">Attainable</th>
                  <th class="text-right py-1.5 font-medium">Override</th>
                  <th class="text-right py-1.5 font-medium">Projected Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in forecastMeasures" :key="m.measure_code"
                  class="border-t border-gray-50"
                  :class="{ 'opacity-40': m.at_ceiling }"
                >
                  <td class="py-1.5 font-medium text-gray-800">{{ m.measure_code }}</td>
                  <td class="py-1.5 text-right" :class="getRateColor(m.current_rate * 100)">
                    {{ (m.current_rate * 100).toFixed(1) }}%
                  </td>
                  <td class="py-1.5 text-right text-gray-600">
                    {{ m.at_ceiling ? '—' : (m.gaps_to_next ?? '—') }}
                  </td>
                  <td class="py-1.5 text-right text-gray-600">{{ m.closeable_gaps ?? '—' }}</td>
                  <td class="py-1.5 text-center">
                    <span v-if="m.at_ceiling" class="text-gray-400 text-[10px]">ceiling</span>
                    <span v-else-if="m.attainable" class="text-emerald-600 font-semibold">✓</span>
                    <span v-else class="text-red-500 font-semibold">✗</span>
                  </td>
                  <td class="py-1.5 text-right">
                    <input
                      v-if="!m.at_ceiling"
                      type="number" min="0"
                      :value="forecastOverrides[m.payer_measure_code] ?? ''"
                      @input="setOverride(m.payer_measure_code, $event.target.value)"
                      class="w-14 text-right text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:ring-1 focus:ring-orange-300 focus:border-orange-300"
                      :placeholder="m.closeable_gaps ?? '0'"
                    />
                  </td>
                  <td class="py-1.5 text-right font-medium"
                    :class="getRateColor(forecastedRate(m) * 100)"
                  >{{ (forecastedRate(m) * 100).toFixed(1) }}%</td>
                </tr>
              </tbody>
            </table>
            <div class="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <button
                @click="runForecast"
                :disabled="forecastLoading"
                class="px-3 py-1.5 text-xs font-medium bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
              >{{ forecastLoading ? 'Calculating...' : 'Recalculate' }}</button>
              <button
                v-if="Object.keys(forecastOverrides).length > 0"
                @click="forecastOverrides = {}; runForecast()"
                class="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
              >Reset overrides</button>
            </div>
          </div>
        </div>

        <!-- Table: grouped/summary view (top level) OR measure breakdown
             (detail view after drilling into a non-measure group). The
             patient list is the separate pane below (v-else). -->
        <div v-if="showGroupTable || showDetailTable" class="flex-1 min-w-0 overflow-auto border border-gray-200 rounded-lg relative" style="min-height: 200px">
        <!-- Loading overlay -->
        <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
          <div class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span class="text-sm text-gray-400">Loading...</span>
          </div>
        </div>

        <table v-if="!loading && activeRows.length === 0" class="w-full text-xs">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th v-for="col in activeColumns" :key="col.field"
                class="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap"
                :class="col.align === 'right' ? 'text-right' : ''"
              >{{ col.header }}</th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-b border-gray-100">
              <td :colspan="activeColumns.length" class="px-3 py-8 text-center text-gray-400">
                No data
              </td>
            </tr>
          </tbody>
        </table>

        <table v-else-if="!loading" class="w-full text-xs">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th v-for="col in activeColumns" :key="col.field"
                class="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap"
                :class="col.align === 'right' ? 'text-right' : ''"
              >{{ col.header }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in activeRows" :key="idx"
              class="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
              :class="{ 'bg-orange-50': isSelectedRow(row) }"
              @click="onRowClick(row)"
            >
              <td v-for="col in activeColumns" :key="col.field"
                class="px-3 py-2 whitespace-nowrap"
                :class="col.align === 'right' ? 'text-right' : ''"
              >
                <!-- Group column with drill arrow -->
                <template v-if="col.field === 'group_1' && showGroupTable">
                  <div class="flex items-center gap-1.5">
                    <font-awesome-icon :icon="['fas', 'caret-right']" class="text-gray-300 text-[10px]" />
                    <span class="font-medium text-gray-800">{{ row[col.field + '_name'] || row[col.field] || 'Unassigned' }}</span>
                    <span v-if="row.methodology"
                      class="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                      :class="getMethodologyStyle(row.methodology)"
                    >{{ getMethodologyLabel(row.methodology) }}</span>
                  </div>
                </template>
                <!-- Rate column -->
                <template v-else-if="col.field === 'rate'">
                  <span :class="getRateColor(row.rate)" class="font-medium">
                    {{ row.rate != null ? row.rate + '%' : '—' }}
                  </span>
                </template>
                <!-- MoM delta column -->
                <template v-else-if="col.field === 'rate_delta'">
                  <span v-if="row.rate_delta != null" class="font-medium"
                    :class="row.rate_delta > 0 ? 'text-emerald-600' : row.rate_delta < 0 ? 'text-red-600' : 'text-gray-400'">
                    <font-awesome-icon v-if="row.rate_delta > 0" :icon="['fas', 'caret-up']" class="mr-0.5" />
                    <font-awesome-icon v-else-if="row.rate_delta < 0" :icon="['fas', 'caret-down']" class="mr-0.5" />
                    {{ row.rate_delta > 0 ? '+' : '' }}{{ row.rate_delta }}%
                  </span>
                  <span v-else class="text-gray-300">—</span>
                </template>
                <!-- YoY delta column -->
                <template v-else-if="col.field === 'yoy_delta'">
                  <span v-if="row.yoy_delta != null" class="font-medium"
                    :class="row.yoy_delta > 0 ? 'text-emerald-600' : row.yoy_delta < 0 ? 'text-red-600' : 'text-gray-400'">
                    <font-awesome-icon v-if="row.yoy_delta > 0" :icon="['fas', 'caret-up']" class="mr-0.5" />
                    <font-awesome-icon v-else-if="row.yoy_delta < 0" :icon="['fas', 'caret-down']" class="mr-0.5" />
                    {{ row.yoy_delta > 0 ? '+' : '' }}{{ row.yoy_delta }}%
                  </span>
                  <span v-else class="text-gray-300">—</span>
                </template>
                <!-- Pacing status column -->
                <template v-else-if="col.field === 'pacing_status'">
                  <span v-if="row.pacing_status"
                    class="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                    :class="{
                      'bg-emerald-100 text-emerald-700': row.pacing_status === 'met',
                      'bg-blue-100 text-blue-700': row.pacing_status === 'on_track',
                      'bg-amber-100 text-amber-700': row.pacing_status === 'at_risk',
                      'bg-red-100 text-red-700': row.pacing_status === 'behind',
                    }"
                  >{{ pacingLabel(row.pacing_status) }}</span>
                  <span v-else class="text-gray-300">—</span>
                </template>
                <!-- Tiers column -->
                <template v-else-if="col.field === 'tiers'">
                  <div v-if="row.tiers && row.tiers.length" class="flex gap-1.5 text-[10px]">
                    <span v-for="t in row.tiers" :key="t.label"
                      class="px-1 py-0.5 rounded font-medium"
                      :class="tierBadgeClass(t, row.rate, row.tiers, row.tiers_inverse)"
                    >{{ t.label.replace('-Star', '★') }} {{ formatTierRate(t, row.tiers_inverse) }}</span>
                  </div>
                  <span v-else class="text-gray-300">—</span>
                </template>
                <!-- Projected rate column -->
                <template v-else-if="col.field === 'projected_rate'">
                  <span v-if="row.projected_rate != null && row.projected_rate !== row.rate"
                    class="font-medium text-blue-600">
                    {{ row.projected_rate }}%
                  </span>
                  <span v-else class="text-gray-300">—</span>
                </template>
                <!-- Score column -->
                <template v-else-if="col.field === 'score'">
                  <span v-if="row.score != null" class="font-medium">
                    {{ formatScore(row) }}
                  </span>
                  <span v-if="row.projected_score != null"
                    class="ml-1 text-blue-600 text-[10px] font-medium">
                    → {{ formatProjectedScore(row) }}
                  </span>
                  <span v-if="row.score == null && row.projected_score == null" class="text-gray-300">—</span>
                </template>
                <!-- Projected closed count — clickable to filter patient list -->
                <template v-else-if="col.field === 'projected_closed_gaps' && showDetailTable">
                  <span v-if="row.projected_closed_gaps > 0"
                    class="text-blue-600 font-medium underline decoration-dotted cursor-pointer hover:text-blue-800"
                    @click.stop="openPatientList(row, 'projected_closed')">
                    {{ row.projected_closed_gaps }}
                  </span>
                  <span v-else class="text-gray-300">0</span>
                </template>
                <!-- Navigate arrow for detail rows → in-pane patient list -->
                <template v-else-if="col.field === '_action' && showDetailTable">
                  <button @click.stop="drillToPatients(row, false)"
                    class="text-orange-500 hover:text-orange-700 cursor-pointer">
                    <font-awesome-icon :icon="['fas', 'arrow-right']" class="text-[10px]" />
                  </button>
                </template>
                <!-- Default -->
                <template v-else>
                  {{ formatCell(row[col.field], col) }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

        <!-- Drill-in = simple patient list straight from ALL_PAYER_CARE_GAPS -->
        <div v-else-if="showPatientList || drillPatients.length || patientsLoading"
             class="flex-1 min-w-0 min-h-0 flex flex-col">
        <div v-if="drillProjectedClosed"
             class="mb-2 flex items-center gap-2 text-xs text-blue-700 bg-blue-50
                    border border-blue-100 rounded px-2.5 py-1.5">
          <font-awesome-icon :icon="['fas', 'filter']" class="text-[10px]" />
          <span>Showing only gaps our logged work is projected to close</span>
          <button class="ml-auto underline hover:no-underline cursor-pointer"
                  @click="clearProjectedClosedScope()">Show all</button>
        </div>
        <div class="mb-2 flex items-center gap-2">
          <span class="text-xs text-gray-400">
            {{ drillPatients.length }} patient gap<template v-if="drillPatients.length !== 1">s</template>
          </span>
          <button v-if="patientGridApi"
                  class="ml-auto text-xs text-gray-500 underline hover:text-gray-700 cursor-pointer"
                  @click="clearPatientGridFilters()">Clear filters</button>
        </div>
        <!-- Explicit viewport-relative height: the shared tab wrapper in
             Quality_Overview.vue has no height, so flex-1 can't resolve here.
             Matches the sibling grids (Clinical_Results_List etc.). -->
        <div class="min-w-0 border border-gray-200 rounded-lg overflow-hidden"
             style="height: calc(100vh - 340px); min-height: 400px">
          <AgGridVue
            :theme="gridTheme"
            class="w-full h-full"
            :column-defs="patientColumnDefs"
            :default-col-def="patientDefaultColDef"
            :row-data="drillPatients"
            :loading="patientsLoading"
            :row-height="34"
            :header-height="36"
            :animate-rows="false"
            @grid-ready="onPatientGridReady"
          />
        </div>
        </div>

      </div><!-- /content column -->
    </div>
  </div>
</template>

<script>
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz } from 'ag-grid-community';
import {
  BUILTIN_PRESETS,
  loadUserViews,
  saveUserViews,
  loadLastViewKey,
  saveLastViewKey,
} from './Quality_ContractPerformance_Views.js';


const AGG_COLUMNS = [
  { header: 'Denom', field: 'denominator', align: 'right' },
  { header: 'Numer', field: 'numerator', align: 'right' },
  { header: 'Rate %', field: 'rate', align: 'right' },
  { header: 'Proj %', field: 'projected_rate', align: 'right' },
  { header: 'Wt', field: 'weight', align: 'right' },
  { header: 'Cutoffs', field: 'tiers', align: 'left' },
  { header: 'Score', field: 'score', align: 'right' },
  { header: 'Measures', field: 'measure_count', align: 'right' },
];

const DETAIL_COLUMNS = [
  { header: 'Contract', field: 'contract_name', align: 'left' },
  { header: 'Measure', field: 'measure_code', align: 'left' },
  { header: 'Name', field: 'measure_name', align: 'left' },
  { header: 'Denom', field: 'denominator', align: 'right' },
  { header: 'Numer', field: 'numerator', align: 'right' },
  { header: 'Rate %', field: 'rate', align: 'right' },
  { header: 'Proj %', field: 'projected_rate', align: 'right' },
  { header: 'Wt', field: 'weight', align: 'right' },
  { header: 'Cutoffs', field: 'tiers', align: 'left' },
  { header: 'MoM Δ', field: 'rate_delta', align: 'right' },
  { header: 'YoY Δ', field: 'yoy_delta', align: 'right' },
  { header: 'Pacing', field: 'pacing_status', align: 'center' },
  { header: 'Open', field: 'open_gaps', align: 'right' },
  { header: 'Closed', field: 'closed_gaps', align: 'right' },
  { header: 'Proj Closed', field: 'projected_closed_gaps', align: 'right' },
  { header: '', field: '_action', align: 'center' },
];

export default {
  name: 'Quality_ContractPerformance',
  components: { ExportMenu, FontAwesomeIcon, Worklist_FilterBar, SimpleMultiSelect, Chart_V5, AgGridVue },
  data() {
    return {
      // Group state — 0 to 2 chips
      groupChips: [],
      pptxLoading: false,
      patientsExporting: false,
      drillPatients: [],
      patientsLoading: false,
      groupRows: [],
      detailRows: [],
      selectedGroup: null,
      // Drill mode: 'detail' shows the measure breakdown for the selected
      // (non-measure) group; 'patients' shows the flat patient list. A
      // non-measure group (Contract/Payer/CPY/Practice) drills to 'detail'
      // first; a measure grain drills straight to 'patients'.
      drillMode: 'detail',
      // When the user clicks into a measure within the detail breakdown, this
      // holds that measure row so the patient fetch + breadcrumb can scope to it.
      selectedDetailRow: null,
      // True when the in-pane patient drill was opened from a projected-closed
      // count, so the list is scoped to gaps our logged work should close.
      drillProjectedClosed: false,

      // --- Detail patient list: AG-Grid (client-side model) ---------------
      // Sortable + filterable on every column. The row set is already capped
      // server-side (limit 2000), so the client-side model is appropriate.
      patientGridApi: null,
      patientDefaultColDef: {
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true,
        flex: 1,
        minWidth: 110,
      },
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        backgroundColor: '#ffffff',
        foregroundColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderRadius: 0,
        fontFamily: 'inherit',
        fontSize: '12px',
        headerBackgroundColor: '#f9fafb',
        headerFontSize: '11px',
        headerFontWeight: 600,
        oddRowBackgroundColor: '#ffffff',
        rowBorder: true,
        rowHoverColor: '#f3f4f6',
        spacing: 6,
        wrapperBorder: false,
      }),
      loading: false,
      lastRefreshDate: null,

      // Entity types for dynamic group options
      entityTypes: [],

      // Filters
      entityFilters: null,
      entityNames: null,  // {typeName: [names]} — for the PPTX scope/title
      infotagFilters: null,
      performanceYears: [new Date().getFullYear()],
      availableYears: [new Date().getFullYear(), new Date().getFullYear() - 1],

      // Contract-family filters (explicit dropdowns, not via entity filter bar)
      selectedContractIds: [],
      selectedPayerNames: [],
      selectedMeasureCodes: [],
      contractEntities: [],  // loaded from /api/entities/?entity_type_id=Contract
      availablePayers: [],   // loaded from Snowflake via operations endpoint
      availableMeasures: [], // loaded from Snowflake via operations endpoint



      // History chart
      historyData: { labels: [], values: [] },
      chartOpen: true,

      // Forecast (shown when drilled into a CPY)
      forecastData: null,
      forecastLoading: false,
      forecastOpen: false,
      forecastOverrides: {},

      // Saved views
      userViews: loadUserViews(),
      currentViewKey: null,
      showViewMenu: false,
      showSaveAsInput: false,
      saveAsName: '',
    };
  },
  computed: {
    builtinViews() {
      return BUILTIN_PRESETS;
    },
    allViews() {
      return [
        ...this.builtinViews,
        ...this.userViews.map(v => ({ ...v, builtin: false })),
      ];
    },
    currentView() {
      return this.allViews.find(v => v.key === this.currentViewKey) || null;
    },
    currentViewLabel() {
      return this.currentView ? this.currentView.name : 'Custom';
    },
    canSaveOverwrite() {
      return !!this.currentView && !this.currentView.builtin;
    },
    viewDirty() {
      if (!this.currentView) return false;
      return JSON.stringify(this._captureCurrentState()) !== JSON.stringify(this.currentView.state);
    },
    yearOptions() {
      return this.availableYears.map(y => ({ value: y, label: String(y) }));
    },

    // ------------------------------------------------------------------
    // Detail patient list: AG-Grid column definitions
    // ------------------------------------------------------------------
    patientColumnDefs() {
      const dateFmt = (p) => {
        if (!p.value) return '';
        const d = new Date(p.value);
        return isNaN(d) ? p.value
          : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      };
      return [
        { headerName: 'Member ID', field: 'patient_identifier', minWidth: 130 },
        {
          headerName: 'Name',
          colId: 'name',
          minWidth: 160,
          valueGetter: (p) => [p.data?.last_name, p.data?.first_name].filter(Boolean).join(', '),
        },
        {
          headerName: 'DOB',
          field: 'dob',
          minWidth: 110,
          filter: 'agDateColumnFilter',
          valueGetter: (p) => (p.data?.dob ? new Date(p.data.dob) : null),
          valueFormatter: dateFmt,
        },
        {
          headerName: 'Measure',
          field: 'measure_code',
          minWidth: 110,
          filter: 'agSetColumnFilter',
        },
        {
          headerName: 'Contract',
          colId: 'contract',
          minWidth: 150,
          filter: 'agSetColumnFilter',
          valueGetter: (p) => p.data?.contract_name || p.data?.contract_id || '',
        },
        {
          headerName: 'Status',
          colId: 'status',
          minWidth: 150,
          filter: 'agSetColumnFilter',
          // One derived value drives display, sort and the set filter, so the
          // three can't disagree.
          valueGetter: (p) => this.patientStatusLabel(p.data),
          cellClass: (p) => {
            const v = p.value;
            if (v === 'Closed') return 'text-emerald-600 font-medium';
            if (v === 'Potentially closed') return 'text-blue-700 font-medium';
            return 'text-gray-600';
          },
        },
        { headerName: 'Provider', field: 'provider_name', minWidth: 150 },
        {
          headerName: 'Last Service',
          field: 'last_service_date',
          minWidth: 120,
          filter: 'agDateColumnFilter',
          valueGetter: (p) => (p.data?.last_service_date ? new Date(p.data.last_service_date) : null),
          valueFormatter: dateFmt,
        },
        {
          headerName: 'Last Work',
          field: 'last_work_date',
          minWidth: 120,
          filter: 'agDateColumnFilter',
          valueGetter: (p) => (p.data?.last_work_date ? new Date(p.data.last_work_date) : null),
          valueFormatter: dateFmt,
        },
        {
          headerName: 'Last Work Outcome',
          field: 'last_work_outcome',
          minWidth: 170,
          filter: 'agSetColumnFilter',
        },
      ];
    },

    groupByOptions() {
      const opts = [
        { value: 'measure', label: 'Measure' },
        { value: 'lob', label: 'LOB' },
        { value: 'emr_access', label: 'EMR Access' },
      ];
      for (const et of this.entityTypes) {
        if (et.is_selectable_grain || et.is_contract_dimension) {
          opts.push({ value: et.id, label: et.type_name });
        }
      }
      return opts;
    },

    excludedEntityTypes() {
      return ['Patient', 'Vendor', 'Contract', 'Contract Pool', 'Contract Performance Year', 'Payer'];
    },
    contractEntityTypeId() {
      const ct = this.entityTypes.find(et => et.type_name === 'Contract' || et.type_name === 'Contract Pool');
      return ct ? ct.id : null;
    },
    contractOptions() {
      return this.contractEntities.map(e => ({ value: e.id, label: e.name }));
    },
    /**
     * Flattened list of the entity (practice/region/etc.) names the deck is
     * scoped to, from the FilterBar. e.g. ["Dominick Basile"].
     */
    scopedEntityNames() {
      if (!this.entityNames) return [];
      const names = [];
      for (const list of Object.values(this.entityNames)) {
        for (const n of (list || [])) {
          if (n) names.push(n);
        }
      }
      return names;
    },
    /**
     * Deck title — the lead/cover heading. When the deck is scoped to specific
     * entities (e.g. a practice), use their name(s); otherwise a generic title.
     */
    deckTitle() {
      const names = this.scopedEntityNames;
      if (names.length === 1) return names[0];
      if (names.length > 1) return `${names.length} Practices`;
      return 'Quality Contract Performance';
    },
    /**
     * Human-readable description of the deck's scope, built from the active
     * filters. Lands in each contract slide's subtitle so a PTM knows what the
     * deck was run for. Falls back to "All Contracts" when nothing is filtered.
     */
    scopeLabel() {
      const parts = [];
      // Lead with the scoped entity name(s) — the most important context.
      if (this.scopedEntityNames.length) {
        parts.push(this.scopedEntityNames.join(', '));
      }
      if (this.selectedContractIds.length) {
        const names = this.selectedContractIds
          .map(id => (this.contractOptions.find(c => c.value === id) || {}).label)
          .filter(Boolean);
        if (names.length) parts.push(names.join(', '));
      }
      if (this.selectedPayerNames.length) {
        parts.push(this.selectedPayerNames.join(', '));
      }
      if (this.selectedMeasureCodes.length) {
        parts.push(`${this.selectedMeasureCodes.length} measure(s)`);
      }
      return parts.length ? parts.join('  ·  ') : 'All Contracts';
    },
    payerOptions() {
      return this.availablePayers.map(p => ({ value: p, label: p }));
    },
    measureOptions() {
      return this.availableMeasures.map(m => ({ value: m.code, label: m.name || m.code }));
    },
    historyChartData() {
      const hd = this.historyData;
      if (!hd.labels.length || !hd.values.length) {
        return { labels: [], series: [] };
      }
      const labels = hd.labels.map(d => {
        const dt = new Date(d + 'T00:00:00');
        return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
      return {
        labels,
        series: [{
          values: hd.values.map(v => v != null ? v / 100 : null),
          config: {
            label: 'Rate',
            backgroundColor: '#2563EB',
            type: 'line',
            yAxis: 'left',
            offset: 0,
            strokeWidth: 2.5,
          },
        }],
      };
    },
    historyChartConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 50, right: 60, bottom: 50, left: 60 },
        xAxis: { labelRotation: -45, fontSize: 10 },
        yAxes: [{ position: 'left', fontSize: 11 }],
        legend: { display: false },
        labelDisplayConfig: { maxLabels: 20, minLabelSpacing: 5 },
        formatting: { percentage: true, sig_figs: 1 },
      };
    },
    infoTagTypes() {
      return [{ type: null, label: 'Tags', buttonLabel: 'Tag+', color: 'emerald' }];
    },
    cpyEntityTypeId() {
      const cpy = this.entityTypes.find(et => et.type_name === 'Contract Performance Year');
      return cpy ? cpy.id : null;
    },
    isDrilledIntoCpy() {
      return this.selectedGroup && this.groupChips.includes(this.cpyEntityTypeId);
    },
    forecastMeasures() {
      if (!this.forecastData?.priorities) return [];
      return this.forecastData.priorities.map(p => ({
        ...p,
        denominator: this.forecastData.forecast?.measures?.find(
          fm => fm.measure_code === p.measure_code
        )?.denominator || 0,
      }));
    },
    forecastEntityId() {
      if (!this.isDrilledIntoCpy) return null;
      const cpyIdx = this.groupChips.indexOf(this.cpyEntityTypeId);
      return this.selectedGroup[`group_${cpyIdx + 1}_id`];
    },
    showGroupTable() {
      return !this.selectedGroup;
    },
    // True when a non-measure group is selected and we're showing the
    // measure breakdown (detail table). False at the top level or when the
    // patient list is showing.
    showDetailTable() {
      return !!this.selectedGroup && this.drillMode === 'detail';
    },
    // True when the patient list pane should render (drilled to patient grain).
    showPatientList() {
      return !!this.selectedGroup && this.drillMode === 'patients';
    },
    // Whether the active grouping is at measure grain (any chip is 'measure').
    // True when the first group chip is the Contract (or Contract Pool) entity
    // type, i.e. the drilled-into group IS one contract — the only case where a
    // single-contract PPTX is meaningful.
    isContractGrouping() {
      const id = this.contractEntityTypeId;
      return !!(id && this.groupChips.length && this.groupChips[0] === id);
    },

    isMeasureGrouping() {
      return this.groupChips.includes('measure');
    },
    // True when every row in the grouped view belongs to a single contract, so
    // contract-specific scoring (tier cutoffs, score) is meaningful. 'contract'
    // is the string chip; a numeric chip is an entity_type_id, and the Contract
    // / Contract Performance Year types also scope to one contract.
    groupingIsContractScoped() {
      return this.groupChips.some((chip) => {
        if (chip === 'contract') return true;
        if (typeof chip !== 'number') return false;
        const label = (this.chipLabel(chip) || '').toLowerCase();
        return label.includes('contract');
      });
    },
    activeColumns() {
      if (this.showDetailTable) return DETAIL_COLUMNS;
      if (!this.showGroupTable) return DETAIL_COLUMNS;
      if (this.groupChips.length === 0) {
        // No grouping — show single aggregate row
        return AGG_COLUMNS;
      }
      const groupCols = this.groupChips.map((chip, i) => ({
        header: this.chipLabel(chip),
        field: `group_${i + 1}`,
        align: 'left',
      }));
      // Build Tracker (Contract Quality Scorecard Bld): "For just the measure
      // and any other filter view currently under the score is all Stars
      // scoring and needs to be removed."
      //
      // Cutoffs/Score are only meaningful inside ONE contract, because each
      // contract scores in its own methodology (stars vs points vs ¢ PMPM). A
      // view grouped by measure — or by anything that is not contract-scoped —
      // aggregates across methodologies, so a star cutoff shown there is
      // meaningless at best and wrong at worst. Drop those two columns unless
      // the grouping is contract-scoped.
      const aggCols = this.groupingIsContractScoped
        ? AGG_COLUMNS
        : AGG_COLUMNS.filter((c) => c.field !== 'tiers' && c.field !== 'score');
      return [...groupCols, ...aggCols];
    },
    activeRows() {
      if (this.showDetailTable) {
        // Detail view: sort alphabetically by contract (display name), then measure.
        // Backend returns CONTRACT_ID/measure_code order, which surfaces as an
        // arbitrary-looking order since CONTRACT_ID is a code, not the name.
        return [...this.detailRows].sort((a, b) => {
          const an = (a.contract_name || a.contract_id || '');
          const bn = (b.contract_name || b.contract_id || '');
          const cmp = an.localeCompare(bn);
          if (cmp !== 0) return cmp;
          return (a.measure_code || '').localeCompare(b.measure_code || '');
        });
      }
      if (this.groupChips.length === 0) return this.groupRows;
      // Sort grouped rows alphabetically by group_1, then group_2
      return [...this.groupRows].sort((a, b) => {
        const a1 = a.group_1 || '';
        const b1 = b.group_1 || '';
        const cmp1 = a1.localeCompare(b1);
        if (cmp1 !== 0 || this.groupChips.length < 2) return cmp1;
        const a2 = a.group_2 || '';
        const b2 = b.group_2 || '';
        return a2.localeCompare(b2);
      });
    },
    groupByLabel() {
      if (this.groupChips.length === 0) return 'All';
      return this.groupChips.map(v => this.chipLabel(v)).join(' × ');
    },
    selectedGroupName() {
      if (!this.selectedGroup) return '';
      return this.groupChips
        .map((_, i) => this.selectedGroup[`group_${i + 1}`] || 'Unassigned')
        .join(' / ');
    },


  },
  async mounted() {
    this.restoreState();
    await this.loadEntityTypes();
    await Promise.all([this.loadContractEntities(), this.loadPayers(), this.loadMeasures()]);
    // Drop any restored selections that don't belong to the current tenant's
    // loaded options (guards against stale cross-tenant state persisted by an
    // older, tenant-agnostic saveState). Must run AFTER options load.
    this.reconcileSelectionsToOptions();
    await Promise.all([this.fetchGroups(), this.fetchHistory()]);
  },
  methods: {
    // ------------------------------------------------------------------
    // Data fetching
    // ------------------------------------------------------------------
    async fetchGroups() {
      this.loading = true;
      try {
        const payload = {
          groupBy: this.groupChips,
          groupKeys: [],
          startRow: 0,
          endRow: 500,
          ...this.buildFilterPayload(),
        };
        const res = await this.$axios.post('/api/stars-performance/operations/', payload);
        this.groupRows = res.data.rows || [];
        this.lastRefreshDate = res.data.last_refresh_date;
      } catch (e) {
        console.error('fetchGroups error:', e);
        this.groupRows = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchHistory() {
      try {
        const payload = this.buildFilterPayload();
        const res = await this.$axios.post('/api/stars-performance/operations/history/', payload);
        this.historyData = {
          labels: res.data.labels || [],
          values: res.data.values || [],
        };
      } catch (e) {
        console.error('fetchHistory error:', e);
        this.historyData = { labels: [], values: [] };
      }
    },

    async fetchDetails() {
      this.loading = true;
      try {
        const groupKeys = this.groupChips.map((_, i) => {
          const idKey = `group_${i + 1}_id`;
          return this.selectedGroup[idKey] != null
            ? this.selectedGroup[idKey]
            : this.selectedGroup[`group_${i + 1}`];
        });
        const payload = {
          groupBy: this.groupChips,
          groupKeys,
          startRow: 0,
          endRow: 500,
          ...this.buildFilterPayload(),
        };
        const res = await this.$axios.post('/api/stars-performance/operations/', payload);
        this.detailRows = res.data.rows || [];
      } catch (e) {
        console.error('fetchDetails error:', e);
        this.detailRows = [];
      } finally {
        this.loading = false;
      }
    },

    async loadEntityTypes() {
      try {
        const res = await this.$axios.get('/api/entity-types/', {
          params: { include_counts: true },
        });
        this.entityTypes = (res.data || []).filter(et => et.entity_count > 0);
      } catch (e) {
        console.error('loadEntityTypes error:', e);
      }
    },

    async loadContractEntities() {
      const contractTypeId = this.contractEntityTypeId;
      if (!contractTypeId) return;
      try {
        const res = await this.$axios.get('/api/entities/', {
          params: { entity_type_id: contractTypeId, page_size: 200 },
        });
        this.contractEntities = (res.data?.results || res.data || [])
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (e) {
        console.error('loadContractEntities error:', e);
      }
    },

    async loadPayers() {
      try {
        const res = await this.$axios.post('/api/stars-performance/operations/', {
          groupBy: ['payer'],
          groupKeys: [],
          startRow: 0,
          endRow: 200,
          performance_year: this.performanceYears,
        });
        this.availablePayers = (res.data.rows || [])
          .map(r => r.group_1)
          .filter(Boolean)
          .sort();
        // If no explicit payers selected yet and MSSP is present, default selection
        // to all non-MSSP (private) payers so MSSP doesn't clutter the default view.
        const hasMssp = this.availablePayers.some(p => p.toUpperCase().includes('MSSP'));
        if (hasMssp && !this.selectedPayerNames.length) {
          this.selectedPayerNames = this.availablePayers.filter(p => !p.toUpperCase().includes('MSSP'));
        }
      } catch (e) {
        console.error('loadPayers error:', e);
      }
    },

    async loadMeasures() {
      try {
        const res = await this.$axios.post('/api/stars-performance/operations/', {
          groupBy: ['measure'],
          groupKeys: [],
          startRow: 0,
          endRow: 500,
          performance_year: this.performanceYears,
        });
        this.availableMeasures = (res.data.rows || [])
          .filter(r => r.group_1)
          .map(r => ({ code: r.group_1, name: r.group_1_name || r.group_1 }))
          .sort((a, b) => a.code.localeCompare(b.code));
      } catch (e) {
        console.error('loadMeasures error:', e);
      }
    },

    buildFilterPayload() {
      const payload = {};
      if (this.entityFilters) payload.entity_filters = this.entityFilters;
      if (this.infotagFilters) payload.infotag_filters = this.infotagFilters;
      if (this.performanceYears.length) payload.performance_year = this.performanceYears;
      if (this.selectedContractIds.length) payload.contract_entity_ids = this.selectedContractIds;
      if (this.selectedPayerNames.length) payload.payer_ids = this.selectedPayerNames;
      if (this.selectedMeasureCodes.length) payload.measure_codes = this.selectedMeasureCodes;
      return payload;
    },

    // ------------------------------------------------------------------
    // Interactions
    // ------------------------------------------------------------------
    toggleChip(value) {
      const idx = this.groupChips.indexOf(value);
      if (idx >= 0) {
        // Deselect
        this.groupChips.splice(idx, 1);
      } else if (this.groupChips.length < 2) {
        // Add
        this.groupChips.push(value);
      } else {
        // At max — replace oldest
        this.groupChips.shift();
        this.groupChips.push(value);
      }
      this.selectedGroup = null;
      this.detailRows = [];
      this.saveState();
      this.fetchGroups();
    },

    chipIndex(value) {
      return this.groupChips.indexOf(value);
    },

    chipClass(value) {
      return this.chipIndex(value) >= 0
        ? 'bg-orange-50 border-orange-300 text-orange-700'
        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300';
    },

    chipLabel(value) {
      const opt = this.groupByOptions.find(o => o.value === value);
      return opt ? opt.label : String(value);
    },

    onRowClick(row) {
      if (this.showGroupTable) {
        // Top level: drill into the group (→ detail breakdown, or patients if
        // grouped by measure).
        this.drillInto(row);
      } else if (this.showDetailTable) {
        // Measure breakdown: click a measure row → its patient list.
        this.drillToPatients(row, false);
      }
    },

    drillInto(group) {
      // Drill behavior depends on the grain of the clicked row:
      //  - Measure grouping (a 'measure' chip is active): the row IS a
      //    measure. Show that measure's performance BROKEN DOWN BY CONTRACT
      //    first (operational view Carrie/Jen asked for), NOT a patient list.
      //    Clicking a contract row in that breakdown then drills to patients.
      //  - Any other grouping (Contract / Payer / CPY / Practice / LOB /
      //    EMR): show the MEASURE BREAKDOWN for that selection first. The
      //    user can then click a measure within the breakdown to see patients.
      this.selectedGroup = group;
      this.selectedDetailRow = null;
      this.forecastData = null;
      this.forecastOverrides = {};
      this.forecastOpen = false;
      if (this.isDrilledIntoCpy) {
        this.loadForecast();
      }
      if (this.isMeasureGrouping) {
        // Per-contract breakdown for the drilled measure.
        this.drillMode = 'detail';
        this.fetchMeasureByContract(group);
      } else {
        this.drillMode = 'detail';
        this.fetchDetails();
      }
    },

    /**
     * From a Measure grouping, drill a measure into its performance BY CONTRACT.
     * Reuses the /operations/ endpoint grouped by the Contract entity type,
     * scoped to the drilled measure (plus the tab's active filters). Populates
     * the same detail table (DETAIL_COLUMNS shows contract_name + rate), so
     * clicking a contract row drills to that contract+measure patient list.
     */
    async fetchMeasureByContract(measureGroup) {
      this.loading = true;
      try {
        const measureCode = measureGroup.group_1_id != null
          ? measureGroup.group_1_id
          : measureGroup.group_1;
        const measureName = measureGroup.group_1_name || measureGroup.measure_name || measureCode;
        const payload = {
          // 'contract' groups directly on CONTRACT_ID (the string the patient
          // drill needs), so no entity-type crosswalk is required.
          groupBy: ['contract'],
          groupKeys: [],
          startRow: 0,
          endRow: 500,
          ...this.buildFilterPayload(),
          // Narrow to the drilled measure regardless of the tab's measure filter.
          measure_codes: [measureCode],
        };
        const res = await this.$axios.post('/api/stars-performance/operations/', payload);
        // Grouped-by-contract rows carry group_1 = CONTRACT_ID + agg cols, but
        // not the DETAIL_COLUMNS fields. Map them into the detail-table shape so
        // the existing table renders (contract_name + this measure + rate), and
        // clicking a row drills to that contract+measure patient list.
        this.detailRows = (res.data.rows || []).map(r => ({
          ...r,
          contract_id: r.group_1,
          contract_name: r.group_1,
          measure_code: measureCode,
          measure_name: measureName,
        }));
      } catch (e) {
        console.error('fetchMeasureByContract error:', e);
        this.detailRows = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * From the measure breakdown (detail table), click a measure row to see
     * its patient list — scoped to the drilled group's contract + that
     * measure. Stays in-pane (no route navigation).
     */
    drillToPatients(detailRow, projectedClosed = null) {
      // The orange arrow drills to all patients for the row; openPatientList()
      // sets drillProjectedClosed first to scope to projected-closed gaps.
      if (projectedClosed !== null) this.drillProjectedClosed = !!projectedClosed;
      this.selectedDetailRow = detailRow;
      this.drillMode = 'patients';
      this.fetchDrillPatientsFromDetail(detailRow);
    },

    /**
     * Fetch the patient list for the drilled-into row from ALL_PAYER_CARE_GAPS.
     * Scope is derived from the active group chips: a Measure chip → that
     * measure (across all contracts); a Contract/CPY chip → that contract;
     * a Payer chip → that payer. Plus the tab's active filters.
     */
    async fetchDrillPatients(group) {
      this.patientsLoading = true;
      this.drillPatients = [];
      this.resetPatientListState();
      try {
        const payload = { ...this.buildFilterPayload() };
        // Map each group chip's value into a concrete filter.
        this.groupChips.forEach((chip, i) => {
          const val = group[`group_${i + 1}`];
          const id = group[`group_${i + 1}_id`];
          const hasId = Object.prototype.hasOwnProperty.call(group, `group_${i + 1}_id`);
          if (chip === 'measure' && val) {
            payload.measure_codes = [val];
          } else if (chip === 'payer' && val) {
            payload.payer_ids = [val];
          } else if (chip === this.contractEntityTypeId || chip === this.cpyEntityTypeId) {
            if (id != null) payload.contract_entity_ids = [id];
          } else if (typeof chip === 'number') {
            // Entity-type group chip (Practice / Pods / Regions / Staff).
            // Scope patients to the clicked entity, or to the Unassigned
            // bucket (no entity of this type resolves) when the id is null.
            payload.entity_type_id = chip;
            if (id != null) {
              payload.entity_id = id;
            } else if (hasId) {
              payload.unassigned = true;
            }
          }
        });
        const res = await this.$axios.post('/api/stars-performance/patients/', payload);
        this.drillPatients = (res.data && res.data.rows) || [];
      } catch (e) {
        console.error('fetchDrillPatients error:', e);
        this.drillPatients = [];
      } finally {
        this.patientsLoading = false;
      }
    },

    /**
     * Fetch the patient list for a measure row clicked within the measure
     * breakdown (detail table). Scope = that detail row's contract + measure,
     * plus the tab's active filters.
     */
    async fetchDrillPatientsFromDetail(detailRow) {
      this.patientsLoading = true;
      this.drillPatients = [];
      this.resetPatientListState();
      try {
        const payload = { ...this.buildFilterPayload() };
        // The detail row carries measure and/or contract depending on the drill
        // direction. In the Measure→Contract breakdown the row is a contract
        // (no measure_code), so fall back to the drilled measure held on
        // selectedGroup. In the Contract→Measure breakdown the row is a measure.
        const rowMeasure = detailRow.measure_code
          || (this.isMeasureGrouping
            ? (this.selectedGroup?.group_1_id != null
              ? this.selectedGroup.group_1_id
              : this.selectedGroup?.group_1)
            : null);
        const rowContract = detailRow.contract_id;
        if (rowMeasure) payload.measure_codes = [rowMeasure];
        if (rowContract) payload.contract_ids = [rowContract];
        if (this.drillProjectedClosed) payload.projected_closed = true;
        const res = await this.$axios.post('/api/stars-performance/patients/', payload);
        this.drillPatients = (res.data && res.data.rows) || [];
      } catch (e) {
        console.error('fetchDrillPatientsFromDetail error:', e);
        this.drillPatients = [];
      } finally {
        this.patientsLoading = false;
      }
    },

    formatDob(val) {
      if (!val) return '';
      const d = new Date(val);
      return isNaN(d) ? val : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    },

    navigateBack() {
      // Two-level drill: if we're viewing patients reached from a measure row
      // within the breakdown, step back to the measure breakdown. Otherwise
      // (top-level patient drill, or the breakdown itself) return to the
      // grouped top-level table.
      this.drillProjectedClosed = false;
      if (this.selectedDetailRow) {
        this.selectedDetailRow = null;
        this.drillMode = 'detail';
        this.drillPatients = [];
        return;
      }
      this.selectedGroup = null;
      this.detailRows = [];
      this.drillMode = 'detail';
      this.drillPatients = [];
      this.forecastData = null;
      this.forecastOverrides = {};
    },

    refetchCurrentLevel() {
      this.saveState();
      this.fetchHistory();
      if (this.showPatientList && this.selectedDetailRow) {
        this.fetchDrillPatientsFromDetail(this.selectedDetailRow);
      } else if (this.showPatientList) {
        this.fetchDrillPatients(this.selectedGroup);
      } else if (this.selectedGroup) {
        this.fetchDetails();
      } else {
        this.fetchGroups();
      }
    },

    // ------------------------------------------------------------------
    // Forecast
    // ------------------------------------------------------------------
    async loadForecast() {
      const entityId = this.forecastEntityId;
      if (!entityId) {
        this.forecastData = null;
        return;
      }
      this.forecastLoading = true;
      try {
        const overrides = Object.keys(this.forecastOverrides).length > 0
          ? this.forecastOverrides : undefined;
        const [forecastRes, prioritizeRes] = await Promise.all([
          this.$axios.post(`/api/stars-performance/contracts/${entityId}/forecast/`, { overrides }),
          this.$axios.get(`/api/stars-performance/contracts/${entityId}/prioritize/`),
        ]);
        this.forecastData = {
          current: forecastRes.data.current,
          forecast: forecastRes.data.forecast,
          priorities: prioritizeRes.data.measures || [],
        };
      } catch (e) {
        console.error('loadForecast error:', e);
        this.forecastData = null;
      } finally {
        this.forecastLoading = false;
      }
    },

    async runForecast() {
      await this.loadForecast();
    },

    setOverride(payer_measure_code, value) {
      if (value === '' || value === null) {
        const copy = { ...this.forecastOverrides };
        delete copy[payer_measure_code];
        this.forecastOverrides = copy;
      } else {
        this.forecastOverrides = { ...this.forecastOverrides, [payer_measure_code]: parseInt(value) || 0 };
      }
    },

    forecastedRate(m) {
      const overrideCount = this.forecastOverrides[m.payer_measure_code];
      if (overrideCount != null && m.denominator > 0) {
        const currentNumer = m.current_rate * m.denominator;
        return (currentNumer + overrideCount) / m.denominator;
      }
      // Use forecast data if available
      const fm = this.forecastData?.forecast?.measures?.find(
        fm => fm.measure_code === m.measure_code
      );
      return fm ? fm.rate / 100 : m.current_rate;
    },

    onEntityFilterChanged(filters) {
      this.entityFilters = filters?.entity_filters || null;
      this.entityNames = filters?.entity_names || null;
      this.infotagFilters = filters?.infotag_filters || null;
      this.refetchCurrentLevel();
    },

    onFilterChange() {
      this.refetchCurrentLevel();
    },

    /**
     * Show the patients behind a clicked count IN-PANE. This tab never
     * navigates away to the Quality Patients worklist — the drill stays here
     * so the user keeps their filters, grouping and place in the table.
     * `projectedClosed` scopes the list to gaps our logged work should close.
     */
    openPatientList(row, projectedClosed = false) {
      this.drillProjectedClosed = !!projectedClosed;
      this.drillToPatients(row);
    },

    /**
     * Canonical status label for a patient-gap row. Single source of truth for
     * the cell text, its colour, the sort order and the column's set filter.
     */
    patientStatusLabel(row) {
      if (!row) return '';
      if (row.compliance_status === 'Compliant') return 'Closed';
      if (row.potentially_closed) return 'Potentially closed';
      return 'Open';
    },

    onPatientGridReady(params) {
      this.patientGridApi = params.api;
    },

    clearPatientGridFilters() {
      if (this.patientGridApi) this.patientGridApi.setFilterModel(null);
    },

    /** Clear any column filters when a new patient list loads. */
    resetPatientListState() {
      this.clearPatientGridFilters();
    },

    /** Drop the projected-closed scope and refetch the full patient list. */
    clearProjectedClosedScope() {
      this.drillProjectedClosed = false;
      if (this.selectedDetailRow) {
        this.fetchDrillPatientsFromDetail(this.selectedDetailRow);
      }
    },

    isSelectedRow() {
      return false;
    },

    // ------------------------------------------------------------------
    // Export
    // ------------------------------------------------------------------
    exportCsv() {
      const rows = this.activeRows;
      const cols = this.activeColumns.filter(c => c.field !== '_action');
      const csvEscape = (val) => {
        if (val == null) return '';
        const str = String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const header = cols.map(c => c.header).join(',');
      const body = rows.map(row =>
        cols.map(c => csvEscape(row[c.field])).join(',')
      ).join('\n');
      const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quality_ops_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },

    /**
     * Export every patient gap for the CURRENT filter scope as a flat CSV —
     * one row per patient × measure × contract, open AND closed, with numeric
     * TIN + NPI for cross-file matching. Hits patients-export/, which streams
     * the whole filtered set server-side rather than the in-pane drill's 2000.
     * Scope = buildFilterPayload() (contracts, payers, measures, performance
     * years, entity + InfoTag filters) — identical to the table.
     */
    async exportPatientsCsv() {
      if (this.patientsExporting) return;
      this.patientsExporting = true;
      try {
        // The server streams the CSV off the Snowflake cursor. Unfiltered this
        // is ~319k rows; fetching it as JSON and assembling it here was a
        // 199 MB response that never completed (req 4841 / Pylon #397).
        const res = await this.$axios.post(
          '/api/stars-performance/patients-export/',
          this.buildFilterPayload(),
          { responseType: 'blob' },
        );
        if (!res.data || res.data.size <= 0) {
          window.alert('No patients match the current filters.');
          return;
        }
        const cd = res.headers && (res.headers['content-disposition']
          || (res.headers.get && res.headers.get('content-disposition')));
        const m = cd && /filename="?([^"]+)"?/.exec(cd);
        const fname = m ? m[1]
          : `quality_patients_${new Date().toISOString().split('T')[0]}.csv`;
        const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Patient CSV export failed', e);
        window.alert('Patient export failed. See console for details.');
      } finally {
        this.patientsExporting = false;
      }
    },

    async exportPptx(onlyContract = null, fmt = 'pptx') {
      const contractTypeId = this.contractEntityTypeId;
      if (!contractTypeId) {
        alert('Contract entity type not found — cannot build the deck.');
        return;
      }
      this.pptxLoading = true;
      try {
        // One call: per-(contract, measure) rows for the current filter set.
        const payload = {
          ...this.buildFilterPayload(),
          groupBy: [contractTypeId, 'measure'],
          groupKeys: [],
          startRow: 0,
          endRow: 5000,
        };
        const res = await this.$axios.post('/api/stars-performance/operations/', payload);
        const rows = (res.data && res.data.rows) || [];

        // Group by contract (group_1 = contract display name, group_1_id = entity).
        const byContract = new Map();
        for (const r of rows) {
          const name = r.group_1 || 'Unassigned';
          if (!byContract.has(name)) {
            byContract.set(name, {
              contract_name: name,
              contract_id: r.group_1_id || null,
              methodology: r.methodology || null,
              rows: [],
            });
          }
          byContract.get(name).rows.push({
            measure_code: r.group_2,
            measure_name: r.group_2_name || r.group_2,
            denominator: r.denominator,
            numerator: r.numerator,
            rate: r.rate,
            target_pct: r.target_pct ?? null,
            score: r.score ?? null,
            max_score: r.max_score ?? null,
            methodology: r.methodology || null,
            pacing_status: r.pacing_status || null,
          });
        }

        // Alphabetical contracts, alphabetical measures within (matches the tab).
        let groups = [...byContract.values()].sort(
          (a, b) => a.contract_name.localeCompare(b.contract_name)
        );
        if (onlyContract) {
          groups = groups.filter(g => g.contract_name === onlyContract);
          if (!groups.length) {
            alert(`No rows for ${onlyContract} under the current filters.`);
            return;
          }
        }
        for (const g of groups) {
          g.rows.sort((a, b) => (a.measure_code || '').localeCompare(b.measure_code || ''));
        }

        const year = this.performanceYears && this.performanceYears.length
          ? this.performanceYears[0] : new Date().getFullYear();

        const isPdf = fmt === 'pdf';
        const pptxRes = await this.$axios.post(
          isPdf ? '/api/stars-performance/export-pdf/'
                : '/api/stars-performance/export-pptx/',
          {
            groups,
            observation_year: year,
            scope_label: this.scopeLabel,
            deck_title: this.deckTitle,
          },
          { responseType: 'blob' }
        );
        const blob = new Blob([pptxRes.data], {
          type: isPdf
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const slug = onlyContract
          ? onlyContract.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toLowerCase() + '_'
          : '';
        a.download = `contract_performance_${slug}${new Date().toISOString().split('T')[0]}.${isPdf ? 'pdf' : 'pptx'}`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('exportPptx error:', e);
        alert(`Failed to build ${fmt === 'pdf' ? 'PDF' : 'PowerPoint'} export.`);
      } finally {
        this.pptxLoading = false;
      }
    },

    // ------------------------------------------------------------------
    // Formatting
    // ------------------------------------------------------------------
    getRateColor(rate) {
      if (rate == null) return 'text-gray-400';
      if (rate >= 80) return 'text-emerald-600';
      if (rate >= 60) return 'text-amber-600';
      return 'text-red-600';
    },

    getMethodologyLabel(methodology) {
      const map = {
        ma_stars: 'Stars',
        min_max_points: 'Points',
        medicaid_tiers: 'Medicaid',
        single_goal: 'Goal',
      };
      return map[methodology] || methodology;
    },

    getMethodologyStyle(methodology) {
      const map = {
        ma_stars: 'bg-orange-100 text-orange-700',
        min_max_points: 'bg-blue-100 text-blue-700',
        medicaid_tiers: 'bg-purple-100 text-purple-700',
        single_goal: 'bg-gray-100 text-gray-700',
      };
      return map[methodology] || 'bg-gray-100 text-gray-600';
    },

    formatProjectedScore(row) {
      if (row.projected_score == null) return '';
      if (row.methodology === 'ma_stars') {
        return `${Number(row.projected_score).toFixed(1)} ★`;
      }
      return `${Number(row.projected_score).toFixed(1)}/${row.max_score || '?'}`;
    },
    formatScore(row) {
      if (row.score == null) return '—';
      if (row.methodology === 'ma_stars') {
        return `${Number(row.score).toFixed(1)} ★`;
      }
      return `${Number(row.score).toFixed(1)}/${row.max_score || '?'}`;
    },

    pacingLabel(status) {
      const labels = { met: 'Met', on_track: 'On Track', at_risk: 'At Risk', behind: 'Behind' };
      return labels[status] || status;
    },
    formatTierRate(tier, isInverse) {
      const rate = this.tierCut(tier, isInverse);
      if (rate == null) return '';
      return Math.round(rate * 100) + '%';
    },
    tierCut(tier, isInverse) {
      return isInverse ? (tier.max_rate ?? tier.min_rate) : (tier.min_rate ?? tier.max_rate);
    },
    tierBadgeClass(tier, rowRate, allTiers, isInverse) {
      // Highlight only the achieved tier (highest score met)
      if (!allTiers || !allTiers.length) return 'bg-gray-100 text-gray-500';
      const rate = (rowRate ?? 0) / 100;
      // Find highest score met
      let achievedScore = 0;
      for (const t of allTiers) {
        const tr = this.tierCut(t, isInverse);
        const met = isInverse ? rate <= tr : rate >= tr;
        if (met && t.score > achievedScore) achievedScore = t.score;
      }
      if (tier.score === achievedScore && achievedScore > 0) return 'bg-emerald-100 text-emerald-700';
      const tierRate = this.tierCut(tier, isInverse);
      const met = isInverse ? rate <= tierRate : rate >= tierRate;
      if (met) return 'bg-emerald-50 text-emerald-500';
      return 'bg-gray-100 text-gray-500';
    },
    formatCell(value, col) {
      if (value == null || value === '') return '—';
      if (typeof value === 'number') {
        if (col.field === 'denominator' || col.field === 'numerator') {
          return Math.round(value).toLocaleString();
        }
        return value.toLocaleString();
      }
      return value;
    },

    formatRefreshDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    // State persistence across tab switches. Keyed PER TENANT — contract IDs
    // and payer names are tenant-specific, so a single global key leaked one
    // tenant's selections into another (wrong "N selected" counts + the backend
    // query being filtered by the other tenant's contracts). See reconcile below.
    stateStorageKey() {
      const slug = this.$store.getters['tenant/current_tenant_slug'] || 'default';
      return `quality_ops_state:${slug}`;
    },
    saveState() {
      const state = {
        groupChips: this.groupChips,
        performanceYears: this.performanceYears,
        selectedContractIds: this.selectedContractIds,
        selectedPayerNames: this.selectedPayerNames,
      };
      localStorage.setItem(this.stateStorageKey(), JSON.stringify(state));
    },
    restoreState() {
      // Migrate/ignore the legacy tenant-agnostic key so old cross-tenant
      // selections never resurface.
      localStorage.removeItem('quality_ops_state');
      const raw = localStorage.getItem(this.stateStorageKey());
      if (!raw) return;
      try {
        const state = JSON.parse(raw);
        if (state.groupChips?.length) this.groupChips = state.groupChips;
        if (state.performanceYears?.length) this.performanceYears = state.performanceYears;
        if (state.selectedContractIds?.length) this.selectedContractIds = state.selectedContractIds;
        if (state.selectedPayerNames?.length) this.selectedPayerNames = state.selectedPayerNames;
      } catch (e) { /* ignore corrupt state */ }
    },
    // Belt-and-suspenders: after the tenant's options load, drop any selected
    // contract IDs / payer names that aren't in the current options. Fixes
    // already-corrupt saved state without the user clearing localStorage.
    reconcileSelectionsToOptions() {
      const validContractIds = new Set(this.contractOptions.map(o => o.value));
      const validPayers = new Set(this.payerOptions.map(o => o.value));
      const c = this.selectedContractIds.filter(id => validContractIds.has(id));
      const p = this.selectedPayerNames.filter(n => validPayers.has(n));
      if (c.length !== this.selectedContractIds.length) this.selectedContractIds = c;
      if (p.length !== this.selectedPayerNames.length) this.selectedPayerNames = p;
    },

    // ------------------------------------------------------------------
    // Saved views & clear filters
    // ------------------------------------------------------------------
    clearAllFilters() {
      this.selectedContractIds = [];
      this.selectedPayerNames = [];
      this.selectedMeasureCodes = [];
      this.performanceYears = [new Date().getFullYear()];
      if (this.$refs.filterBarRef?.clearAll) {
        this.$refs.filterBarRef.clearAll();
      }
      this.entityFilters = null;
      this.entityNames = null;
      this.saveState();
      this.onFilterChange();
    },
    closeViewMenu() {
      this.showViewMenu = false;
      this.cancelSaveAs();
    },
    selectView(key) {
      const view = this.allViews.find(v => v.key === key);
      if (!view) return;
      this._applyViewState(view.state);
      this.currentViewKey = key;
      this.showViewMenu = false;
      saveLastViewKey(key);
      this.onFilterChange();
    },
    _captureCurrentState() {
      return {
        performanceYears: [...this.performanceYears],
        selectedContractIds: [...this.selectedContractIds],
        selectedPayerNames: [...this.selectedPayerNames],
        selectedMeasureCodes: [...this.selectedMeasureCodes],
        groupChips: [...this.groupChips],
      };
    },
    _applyViewState(s) {
      if (s.performanceYears) this.performanceYears = [...s.performanceYears];
      if (s.selectedContractIds) this.selectedContractIds = [...s.selectedContractIds];
      if (s.selectedPayerNames) this.selectedPayerNames = [...s.selectedPayerNames];
      if (s.selectedMeasureCodes) this.selectedMeasureCodes = [...s.selectedMeasureCodes];
      if (s.groupChips) this.groupChips = [...s.groupChips];
      this.saveState();
    },
    openSaveAs() {
      this.saveAsName = '';
      this.showSaveAsInput = true;
      this.$nextTick(() => {
        if (this.$refs.saveAsInput) this.$refs.saveAsInput.focus();
      });
    },
    cancelSaveAs() {
      this.showSaveAsInput = false;
      this.saveAsName = '';
    },
    confirmSaveAs() {
      const name = this.saveAsName.trim();
      if (!name) return;
      const key = `user_${Date.now()}`;
      this.userViews.push({ key, name, state: this._captureCurrentState() });
      saveUserViews(this.userViews);
      this.currentViewKey = key;
      saveLastViewKey(key);
      this.cancelSaveAs();
      this.showViewMenu = false;
    },
    overwriteCurrentView() {
      if (!this.canSaveOverwrite) return;
      const idx = this.userViews.findIndex(v => v.key === this.currentViewKey);
      if (idx < 0) return;
      this.userViews[idx] = {
        ...this.userViews[idx],
        state: this._captureCurrentState(),
      };
      saveUserViews(this.userViews);
      this.showViewMenu = false;
    },
    deleteUserView(key) {
      this.userViews = this.userViews.filter(v => v.key !== key);
      saveUserViews(this.userViews);
      if (this.currentViewKey === key) {
        this.currentViewKey = null;
        saveLastViewKey(null);
      }
    },
  },
};
</script>
